import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useRoleUsers } from '../hooks/useRoleUsers';
import { useAuth } from '../contexts/AuthContext';
import AppLayout from '../components/AppLayout';
import toast from 'react-hot-toast';
import { Product } from '../types/Product';
import { buildPriceExportRows } from '../lib/priceExport';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  X, 
  Package, 
  DollarSign, 
  Check, 
  Clock, 
  AlertCircle,
  Save,
  CheckCircle,
  Search,
  Download
} from 'lucide-react';
import * as XLSX from 'xlsx';

type TabType = 'all' | 'missing' | 'entered' | 'completed';

interface PriceStats {
  missing: number;
  entered: number;
  completed: number;
  total: number;
}

const PricesDashboard: React.FC = () => {
  const { getUsersByRole } = useRoleUsers();
  const { user } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('missing');
  const [stats, setStats] = useState<PriceStats>({ missing: 0, entered: 0, completed: 0, total: 0 });
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Search & filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [manufacturerFilter, setManufacturerFilter] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    costPrice: '',
    salePrice: '',
    discountPrice: ''
  });

  const loadStats = useCallback(async () => {
    try {
      const response = await axios.get('/products/price-stats');
      setStats(response.data);
    } catch (error) {
      console.error('İstatistikler yüklenemedi:', error);
    }
  }, []);

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      let endpoint = '/products';
      
      switch (activeTab) {
        case 'missing':
          endpoint = '/products/price-status/missing';
          break;
        case 'entered':
          endpoint = '/products/price-status/entered';
          break;
        case 'completed':
          endpoint = '/products/price-status/completed';
          break;
        default:
          endpoint = '/products';
      }
      
      const response = await axios.get(endpoint);
      setProducts(response.data || []);
    } catch (error) {
      toast.error('Ürünler yüklenemedi!');
      console.error('Load products error:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    loadProducts();
    loadStats();
  }, [loadProducts, loadStats]);

  const handleEditPrice = (product: Product) => {
    setSelectedProduct(product);
    setFormData({
      costPrice: product.productionInfo?.costPrice?.toString() || '',
      salePrice: product.productionInfo?.salePrice?.toString() || '',
      discountPrice: product.productionInfo?.discountPrice?.toString() || ''
    });
    setShowModal(true);
  };

  const handleSavePrice = async () => {
    if (!selectedProduct || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const data: any = {};
      
      // parseFloat ile NaN ve negatif değer kontrolü
      if (formData.costPrice) {
        const parsed = parseFloat(formData.costPrice);
        if (!isNaN(parsed) && parsed >= 0) {
          data.costPrice = parsed;
        }
      }
      if (formData.salePrice) {
        const parsed = parseFloat(formData.salePrice);
        if (!isNaN(parsed) && parsed >= 0) {
          data.salePrice = parsed;
        } else {
          toast.error('Geçerli bir satış fiyatı giriniz!');
          setIsSubmitting(false);
          return;
        }
      }
      if (formData.discountPrice) {
        const parsed = parseFloat(formData.discountPrice);
        if (!isNaN(parsed) && parsed >= 0) {
          data.discountPrice = parsed;
        }
      }

      await axios.post(`/products/update-price/${selectedProduct._id}`, data);
      
      toast.success('Fiyat başarıyla kaydedildi!');
      setShowModal(false);
      setSelectedProduct(null);

      if (activeTab === 'completed') {
        setActiveTab('entered');
      } else {
        loadProducts();
        loadStats();
      }
    } catch (error: any) {
      toast.error('Fiyat kaydedilemedi: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMarkNebimUpdated = async (productId: string) => {
    try {
      await axios.post(`/products/mark-nebim-updated/${productId}`);
      toast.success('Nebim güncelleme işaretlendi!');
      loadProducts();
      loadStats();
    } catch (error: any) {
      toast.error('İşlem başarısız: ' + (error.response?.data?.message || error.message));
    }
  };

  const resetForm = () => {
    setFormData({ costPrice: '', salePrice: '', discountPrice: '' });
    setSelectedProduct(null);
    setShowModal(false);
  };

  const manufacturers = Array.from(
    new Set(products.map(p => p.productionInfo?.manufacturer).filter(Boolean))
  ).sort() as string[];

  const filteredProducts = products.filter(p => {
    if (manufacturerFilter && (p.productionInfo?.manufacturer || '') !== manufacturerFilter) {
      return false;
    }
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (p.productionInfo?.productName || '').toLowerCase().includes(term) ||
      (p.nebimInfo?.sku || '').toLowerCase().includes(term) ||
      (p.productionInfo?.manufacturer || '').toLowerCase().includes(term)
    );
  });

  const handleExcelExport = () => {
    if (filteredProducts.length === 0) {
      toast.error('Dışa aktarılacak ürün bulunamadı!');
      return;
    }

    const rows = buildPriceExportRows(filteredProducts);
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const colWidths = [18, 12, 14, 12, 14, 14, 28, 20, 14, 12];
    ws['!cols'] = colWidths.map(w => ({ wch: w }));

    const wb = XLSX.utils.book_new();
    const tabLabel = tabs.find(t => t.key === activeTab)?.label || 'tum';
    XLSX.utils.book_append_sheet(wb, ws, tabLabel);
    const dateStr = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `fiyat_listesi_${activeTab}_${dateStr}.xlsx`);
    toast.success(`${filteredProducts.length} ürün Excel'e aktarıldı!`);
  };

  const tabs: { key: TabType; label: string; count: number; icon: React.ReactNode }[] = [
    { key: 'all', label: 'Tüm Ürünler', count: stats.total, icon: <Package className="w-4 h-4" /> },
    { key: 'missing', label: 'Fiyatı Eksik', count: stats.missing, icon: <AlertCircle className="w-4 h-4" /> },
    { key: 'entered', label: 'Fiyatı Girilen', count: stats.entered, icon: <Clock className="w-4 h-4" /> },
    { key: 'completed', label: 'Tamamlanan', count: stats.completed, icon: <Check className="w-4 h-4" /> },
  ];

  return (
    <AppLayout
      title="Fiyat Yönetimi"
      subtitle={`Ürün Fiyat Girişi ve Nebim Güncelleme - ${getUsersByRole('production') || 'Üretim'}/${getUsersByRole('nebim') || 'Nebim'}`}
    >
      <div className="container mx-auto py-8 space-y-6 px-4">
        {/* Bilgi Kartı */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <DollarSign className="h-5 w-5 text-amber-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-amber-900">Görevleriniz</h3>
              <p className="text-sm text-amber-700 mt-1">
                Ürünlere maliyet ve satış fiyatlarını giriniz. Fiyat girildikten sonra Nebim'de güncelleme yapıp "Nebim'de Güncellendi" butonuna tıklayınız.
              </p>
              <p className="text-sm text-amber-700 mt-1">
                Üretimin RPT olarak işaretlediği ürünleri tabloda ve fiyat Excel'indeki "RPT Ürün" sütununda görebilirsiniz.
              </p>
            </div>
          </div>
        </div>

        {/* Tab Bar */}
        <div className="flex flex-wrap items-center gap-2">
          {tabs.map(tab => (
            <Button
              key={tab.key}
              variant={activeTab === tab.key ? 'default' : 'outline'}
              onClick={() => setActiveTab(tab.key)}
              className="gap-2"
            >
              {tab.icon}
              {tab.label}
              <Badge variant={activeTab === tab.key ? 'secondary' : 'outline'}>
                {tab.count}
              </Badge>
            </Button>
          ))}
          <div className="ml-auto">
            <Button variant="outline" size="sm" onClick={handleExcelExport} className="gap-2">
              <Download className="w-4 h-4" />
              Excel'e Aktar
            </Button>
          </div>
        </div>

        {/* Filtreler */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Ürün adı, SKU veya üretici ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-9 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div>
            <select
              value={manufacturerFilter}
              onChange={(e) => setManufacturerFilter(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring bg-background"
            >
              <option value="">Tüm Üreticiler</option>
              {manufacturers.map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            {(searchTerm || manufacturerFilter) && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => { setSearchTerm(''); setManufacturerFilter(''); }}
              >
                Filtreleri Temizle
              </Button>
            )}
          </div>
        </div>

        {/* Ürün Tablosu */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center justify-between">
              <span>{tabs.find(t => t.key === activeTab)?.label}</span>
              {(searchTerm || manufacturerFilter) && (
                <span className="text-sm font-normal text-muted-foreground">
                  {filteredProducts.length} / {products.length} ürün
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">
                  {(searchTerm || manufacturerFilter) ? 'Filtreyle eşleşen ürün bulunamadı' : 'Bu kategoride ürün bulunamadı'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-3 font-medium">Ürün Adı</th>
                      <th className="text-left p-3 font-medium">SKU</th>
                      <th className="text-center p-3 font-medium">RPT Ürün</th>
                      <th className="text-right p-3 font-medium">Maliyet</th>
                      <th className="text-right p-3 font-medium">Satış Fiyatı</th>
                      <th className="text-right p-3 font-medium">İndirimli Fiyat</th>
                      <th className="text-center p-3 font-medium">Durum</th>
                      <th className="text-center p-3 font-medium">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.map(product => (
                      <tr key={product._id} className="border-b hover:bg-muted/30">
                        <td className="p-3">
                          <div className="font-medium">
                            {product.productionInfo?.productName || 'İsimsiz Ürün'}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {product.productionInfo?.manufacturer}
                          </div>
                        </td>
                        <td className="p-3 font-mono text-xs">
                          {product.nebimInfo?.sku || '-'}
                        </td>
                        <td className="p-3 text-center">
                          {product.isRPT === true ? (
                            <Badge className="bg-purple-100 text-purple-800">Evet</Badge>
                          ) : (
                            <span className="text-muted-foreground">Hayır</span>
                          )}
                        </td>
                        <td className="p-3 text-right">
                          {product.productionInfo?.costPrice 
                            ? `₺${product.productionInfo.costPrice.toLocaleString('tr-TR')}` 
                            : '-'}
                        </td>
                        <td className="p-3 text-right">
                          {product.productionInfo?.salePrice 
                            ? `₺${product.productionInfo.salePrice.toLocaleString('tr-TR')}` 
                            : '-'}
                        </td>
                        <td className="p-3 text-right">
                          {product.productionInfo?.discountPrice 
                            ? `₺${product.productionInfo.discountPrice.toLocaleString('tr-TR')}` 
                            : '-'}
                        </td>
                        <td className="p-3 text-center">
                          {product.nebimPriceUpdated ? (
                            <Badge className="bg-green-100 text-green-800">Tamamlandı</Badge>
                          ) : product.priceEntered ? (
                            <Badge className="bg-yellow-100 text-yellow-800">Fiyat Girildi</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800">Fiyat Eksik</Badge>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          <div className="flex justify-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditPrice(product)}
                            >
                              <DollarSign className="w-4 h-4 mr-1" />
                              {product.nebimPriceUpdated ? 'Fiyat Düzenle' : 'Fiyat Gir'}
                            </Button>
                            {product.priceEntered && !product.nebimPriceUpdated && user?.role !== 'production' && (
                              <Button
                                size="sm"
                                variant="default"
                                onClick={() => handleMarkNebimUpdated(product._id)}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Nebim OK
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Fiyat Girişi Modal */}
      {showModal && selectedProduct && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{selectedProduct?.nebimPriceUpdated ? 'Fiyat Düzenleme' : 'Fiyat Girişi'}</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedProduct.productionInfo?.productName}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={resetForm}
                className="h-8 w-8 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Maliyet Fiyatı (TL)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.costPrice}
                  onChange={(e) => setFormData(prev => ({ ...prev, costPrice: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Satış Fiyatı (TL) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.salePrice}
                  onChange={(e) => setFormData(prev => ({ ...prev, salePrice: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="0.00"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">İndirimli Fiyat (TL)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.discountPrice}
                  onChange={(e) => setFormData(prev => ({ ...prev, discountPrice: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="0.00"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  className="flex-1"
                  onClick={handleSavePrice}
                  disabled={isSubmitting || !formData.salePrice}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
                <Button
                  variant="outline"
                  onClick={resetForm}
                  disabled={isSubmitting}
                >
                  İptal
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </AppLayout>
  );
};

export default PricesDashboard;
